import * as THREE from "three";
import {RaceRenderOrder} from "../../../raceRenderOrder.js";
import {RacerFlare} from "../flare/racerFlare.js";

/**
 * A trail to show behind a racer
 */
export class RacerTrail {
    static SHADER_VERTEX = `
        attribute vec2 offset;
        attribute vec3 center;
        attribute vec3 direction;
        
        varying vec2 vUv;
        
        void main() {
            vec3 up = normalize(cross(cameraPosition - center, direction)) * pow(offset.x, 0.3);
            
            vUv = offset;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(center + up * offset.y, 1.0);
        }
        `;
    static SHADER_FRAGMENT = `
        varying vec2 vUv;
        
        void main() {
            float multiplier = 1.0;
            float longitude = 0.4;
            float latitude = 0.4;
            float alpha = sin(3.141593 * pow(vUv.x, longitude)) * pow(cos(vUv.y * 1.570796), latitude) * multiplier;
            
            gl_FragColor = vec4(mix(vec3(0.0, 0.5, 1.0), vec3(1.0), pow(alpha, 5.0)), alpha);
        }
        `;
    
    static SHADER_FRAGMENT_RED = `
        varying vec2 vUv;
        
        void main() {
            float multiplier = 1.0;
            float longitude = 0.4;
            float latitude = 0.4;
            float alpha = sin(3.141593 * pow(vUv.x, longitude)) * pow(cos(vUv.y * 1.570796), latitude) * multiplier;
            
            gl_FragColor = vec4(mix(vec3(1.0, 0.0, 0.0), vec3(1.0), pow(alpha, 5.0)), alpha);
        }
        `;
    
    static SHADER_FRAGMENT_GOLD = `
        varying vec2 vUv;
        
        void main() {
            float multiplier = 1.0;
            float longitude = 0.4;
            float latitude = 0.4;
            float alpha = sin(3.141593 * pow(vUv.x, longitude)) * pow(cos(vUv.y * 1.570796), latitude) * multiplier;
            
            gl_FragColor = vec4(mix(vec3(1.0, 1.0, 0.0), vec3(1.0), pow(alpha, 5.0)), alpha);
        }
        `;
    

    static SHADER_FRAGMENT_GREEN = `
        varying vec2 vUv;
        
        void main() {
            float multiplier = 1.0;
            float longitude = 0.4;
            float latitude = 0.4;
            float alpha = sin(3.141593 * pow(vUv.x, longitude)) * pow(cos(vUv.y * 1.570796), latitude) * multiplier;
            
            gl_FragColor = vec4(mix(vec3(0.0, 1.0, 0.0), vec3(1.0), pow(alpha, 5.0)), alpha);
        }
        `;

    static SEGMENTS = 128;
    static SPACING = 1;

    /**
     * Make the trail geometry
     * @param {THREE.InterleavedBufferAttribute} centroids The centroids for this mesh
     * @param {THREE.InterleavedBufferAttribute} directions The directions
     * @returns {THREE.BufferGeometry} The generic trail geometry
     */
    static makeGeometry(centroids, directions) {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        for (let segment = 0; segment < RacerTrail.SEGMENTS; ++segment) {
            vertices.push(
                segment / (RacerTrail.SEGMENTS - 1), -1,
                segment / (RacerTrail.SEGMENTS - 1), 1);

            if (segment !== RacerTrail.SEGMENTS - 1)
                indices.push(
                    segment * 2,
                    segment * 2 + 1,
                    (segment + 1) * 2,
                    (segment + 1) * 2,
                    (segment + 1) * 2 + 1,
                    segment * 2 + 1);
        }

        geometry.setAttribute(
            "offset",
            new THREE.BufferAttribute(new Float32Array(vertices), 2));
        geometry.setAttribute(
            "center",
            centroids);
        geometry.setAttribute(
            "direction",
            directions);
        geometry.setIndex(indices);

        return geometry;
    }

    /**
     * Make the trail material
     * @returns {THREE.ShaderMaterial} The trail material
     */
    static makeMaterial(specialFlag) {
        if(specialFlag >= 10) {
            return new THREE.ShaderMaterial({
                uniforms: {
    
                },
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                side: THREE.DoubleSide,
                vertexShader: RacerTrail.SHADER_VERTEX,
                fragmentShader: RacerTrail.SHADER_FRAGMENT_GOLD
            });
        } else if(specialFlag > 0) {
            return new THREE.ShaderMaterial({
                uniforms: {
    
                },
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                side: THREE.DoubleSide,
                vertexShader: RacerTrail.SHADER_VERTEX,
                fragmentShader: RacerTrail.SHADER_FRAGMENT_GREEN
            });
        } else {
            return new THREE.ShaderMaterial({
                uniforms: {
    
                },
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                side: THREE.DoubleSide,
                vertexShader: RacerTrail.SHADER_VERTEX,
                fragmentShader: RacerTrail.SHADER_FRAGMENT
            });
        }
        
    }

    /**
     * Construct a racer trail
     * @param {RacerTrailProperties} properties The trail properties
     * @param {THREE.Vector3} offset The offset relative to the ship
     */
    constructor(properties, offset, specialRacer) {
        this.properties = properties;
        this.offset = offset;
        this.offsetRotated = new THREE.Vector3();
        this.trailData = new Float32Array(12 * RacerTrail.SEGMENTS);
        this.trailBuffer = new THREE.InterleavedBuffer(this.trailData, 6);
        this.trailBuffer.usage = THREE.DynamicDrawUsage;
        this.centroids = new THREE.InterleavedBufferAttribute(this.trailBuffer, 3, 0);
        this.directions = new THREE.InterleavedBufferAttribute(this.trailBuffer, 3, 3);
        this.head = new THREE.Vector3();
        this.direction = new THREE.Vector3();
        this.geometry = RacerTrail.makeGeometry(this.centroids, this.directions);
        this.material = RacerTrail.makeMaterial(specialRacer);
        this.flare = new RacerFlare();
        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.mesh.position.set(0, 0, 0);
        this.mesh.frustumCulled = false;
        this.mesh.visible = true;
        this.mesh.renderOrder = RaceRenderOrder.ORDER_TRAILS;
        this.mesh.add(this.flare.sprite);
        this.specialRacer = specialRacer;
    }

    /**
     * The centroids have changed
     */
    dataChanged() {
        this.geometry.getAttribute("center").needsUpdate = true;
    }

    /**
     * Teleport the trail to a position
     * @param {THREE.Vector3} position The position to teleport to
     */
    teleport(position) {
        this.head.copy(position);

        for (let segment = 0; segment < RacerTrail.SEGMENTS; ++segment) {
            this.trailData[segment * 12] = this.trailData[segment * 12 + 6] = position.x;
            this.trailData[segment * 12 + 1] = this.trailData[segment * 12 + 7] = position.y;
            this.trailData[segment * 12 + 2] = this.trailData[segment * 12 + 8] = position.z;
        }

        this.dataChanged();
    }

    /**
     * Move the head of this trail
     * @param {THREE.Vector3} position The new head position
     * @param {THREE.Quaternion} rotation The ship rotation
     */
    moveHead(position, rotation) {
        let distance = position.distanceTo(this.head);

        this.direction.copy(position).sub(this.head).normalize();

        while (distance > RacerTrail.SPACING) {
            this.head.addScaledVector(this.direction, RacerTrail.SPACING);

            distance -= RacerTrail.SPACING;
        }

        this.offsetRotated.copy(this.offset).applyQuaternion(rotation);
        this.flare.sprite.position.copy(position).add(this.offsetRotated);

        this.shiftData();

        this.trailData[0] = this.trailData[6] = position.x + this.offsetRotated.x;
        this.trailData[1] = this.trailData[7] = position.y + this.offsetRotated.y;
        this.trailData[2] = this.trailData[8] = position.z + this.offsetRotated.z;
        this.trailData[3] = this.trailData[9] = this.direction.x;
        this.trailData[4] = this.trailData[10] = this.direction.y;
        this.trailData[5] = this.trailData[11] = this.direction.z;

        this.dataChanged();
    }

    /**
     * Shift the latest head position into the centroid array
     */
    shiftData() {
        // TODO: Copy in place?
        for (let segment = RacerTrail.SEGMENTS; segment-- > 1;) {
            this.trailData[segment * 12] = this.trailData[segment * 12 + 6] = this.trailData[segment * 12 - 6];
            this.trailData[segment * 12 + 1] = this.trailData[segment * 12 + 7] = this.trailData[segment * 12 - 5];
            this.trailData[segment * 12 + 2] = this.trailData[segment * 12 + 8] = this.trailData[segment * 12 - 4];
            this.trailData[segment * 12 + 3] = this.trailData[segment * 12 + 9] = this.trailData[segment * 12 - 3];
            this.trailData[segment * 12 + 4] = this.trailData[segment * 12 + 10] = this.trailData[segment * 12 - 2];
            this.trailData[segment * 12 + 5] = this.trailData[segment * 12 + 11] = this.trailData[segment * 12 - 1];
        }
    }


    /**
     * Change Color of this trail for Aggression
     * @param {THREE.Vector3} position The new head position
     * @param {THREE.Quaternion} rotation The ship rotation
     */
     showAggressionBoost(onflag) {
        if (onflag == 1) {
            this.material = new THREE.ShaderMaterial({
                uniforms: {
    
                },
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                side: THREE.DoubleSide,
                vertexShader: RacerTrail.SHADER_VERTEX,
                fragmentShader: RacerTrail.SHADER_FRAGMENT_RED
            });

            this.mesh.material = this.material;
        } else {
            if(this.specialRacer >= 10) {
                this.material = new THREE.ShaderMaterial({
                    uniforms: {
        
                    },
                    blending: THREE.AdditiveBlending,
                    depthWrite: false,
                    side: THREE.DoubleSide,
                    vertexShader: RacerTrail.SHADER_VERTEX,
                    fragmentShader: RacerTrail.SHADER_FRAGMENT_GOLD
                });
                this.mesh.material = this.material; 
            } else if(this.specialRacer > 0) {
                this.material = new THREE.ShaderMaterial({
                    uniforms: {
        
                    },
                    blending: THREE.AdditiveBlending,
                    depthWrite: false,
                    side: THREE.DoubleSide,
                    vertexShader: RacerTrail.SHADER_VERTEX,
                    fragmentShader: RacerTrail.SHADER_FRAGMENT_GREEN
                });
                this.mesh.material = this.material; 
            } else {
                this.material = new THREE.ShaderMaterial({
                    uniforms: {
        
                    },
                    blending: THREE.AdditiveBlending,
                    depthWrite: false,
                    side: THREE.DoubleSide,
                    vertexShader: RacerTrail.SHADER_VERTEX,
                    fragmentShader: RacerTrail.SHADER_FRAGMENT
                });
                this.mesh.material = this.material; 
            }
            
        }
        
        this.dataChanged();
    }
}